import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';

// LEARNING REPORT (admin / support only) — what each camera has taught itself.
//
// Scene memory and perspective both learn silently into sidecar files, and before
// this the only way to read either was SSH plus scene_memory_tool.py. The question
// this page answers is "is this install getting better", and the one it answers by
// accident is more important: a camera too far or too dark for pose to resolve
// accumulates REAL people as 0-skeleton alerts, and those cells drift toward
// suppressing a siren. The `watch` column is how that is caught early, so it is
// given a colour and a footnote rather than being buried in the cell list.
//
// Authorization lives on the endpoint (_verify_admin -> 403). The role check here
// is only so a non-admin sees an explanation instead of an error.

const td  = { padding: '8px 12px', verticalAlign: 'top' };
const th  = { padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'left' };
const pill = (bg, fg) => ({
  background: bg, color: fg, borderRadius: 6, padding: '1px 7px',
  fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
});

// Kept only as a record of what the customer once answered. It decides nothing:
// the siren window already covers "not at these hours", and marking a camera
// "we use this at night" silenced it for strangers too. See main.py.
const POSTURE_LABEL = {
  never:  'nobody at night (not used)',
  normal: 'we use this at night (not used)',
};

// What each threshold means, in the words someone tuning a live site needs. These
// were set from two sites' data (B140 and B144) — the first thing a new customer
// may need is a different one, and that must not cost a release.
const TUNABLE_HELP = {
  min_occurrences:      'alerts at one spot before the siren is dropped there',
  min_span_days:        'those alerts must span at least this many days',
  min_attempts:         'blocked detections at one spot in a night before the siren is dropped',
  min_attempt_span_hrs: 'those blocked detections must span at least this many hours',
  attempt_hour_start:   'start of the night window (24h clock)',
  attempt_hour_end:     'end of the night window (24h clock)',
  skel_trust_min:       'keypoint sightings needed to outrank an active storm',
  attempt_window_hrs:   'a gap longer than this starts the night count again',
  min_cam_attempts:     'blocked detections across the whole camera before it counts as a storm',
  min_cam_span_hrs:     'those must span at least this many hours',
  max_cells_per_cam:    'how many spots a camera remembers at once',
  animal_skip_conf:     'animal confidence above which an alert does NOT build a spot',
};

// The alert photo behind a dropped siren. The image route needs the bearer
// token, so it cannot be an <img src>; fetch it and hand the tag a blob URL.
// Same approach as AlertsPage, kept local rather than exported — this page
// shows a handful of thumbnails, not a gallery.
function AuthThumb({ file, alt, boxes, width = 148, height = 94 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    apiFetch(`/alerts/${encodeURIComponent(file)}/image`)
      .then(r => (r.ok ? r.blob() : null))
      .then(b => { if (b) { url = URL.createObjectURL(b); setSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [file]);
  const box = { width, height, borderRadius: 8, objectFit: 'cover',
                border: '1px solid var(--border)', background: 'var(--surface)' };
  if (!src) return <div style={{ ...box, display: 'flex', alignItems: 'center',
                                 justifyContent: 'center', fontSize: 11,
                                 color: 'var(--text-muted)' }}>no photo</div>;
  // ev_bbox is the PHOTO's own normalised box, so an overlay lines up with what
  // the detector actually saw -- which is the whole point of showing it.
  return (
    <div style={{ position: 'relative', width, height }}>
      <img src={src} alt={alt} style={{ ...box, cursor: 'zoom-in' }}
           onClick={() => window.open(src, '_blank')} />
      {(boxes || []).map((b, i) => b.r && (
        <div key={i} style={{
          position: 'absolute', pointerEvents: 'none',
          left:   `${b.r[0] * 100}%`, top:    `${b.r[1] * 100}%`,
          width:  `${(b.r[2] - b.r[0]) * 100}%`,
          height: `${(b.r[3] - b.r[1]) * 100}%`,
          border: `2px solid ${b.color}`, borderRadius: 3,
          boxShadow: b.dashed ? 'none' : '0 0 0 1px rgba(0,0,0,.35)',
          borderStyle: b.dashed ? 'dashed' : 'solid',
        }} />
      ))}
    </div>
  );
}

// WHAT BUILT THIS SPOT. "Cell 7,2" is unreadable; the alerts behind it are not,
// and a suppressed alert is still a stored alert, so they are all on disk.
function SpotPanel({ camera, cell, onClose, onReleased, releasing, onRelease }) {
  const [d, setD]   = useState(null);
  const [e, setE]   = useState('');
  useEffect(() => {
    let dead = false;
    setD(null); setE('');
    apiFetch(`/ui/learning/spot?camera=${encodeURIComponent(camera)}&cell=${encodeURIComponent(cell)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(j => { if (!dead) setD(j); })
      .catch(x => { if (!dead) setE(String(x.message || x)); });
    return () => { dead = true; };
  }, [camera, cell]);

  return (
    <div style={{ border: '1px solid #2563eb', borderRadius: 10,
                  padding: '12px 14px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>{camera} — spot {cell}</strong>
        <span style={{ display: 'flex', gap: 8 }}>
          <button type="button" disabled={releasing === `${camera}|${cell}`}
                  onClick={async () => { await onRelease(camera, cell); onReleased?.(); }}
                  style={{ fontSize: 12, padding: '2px 10px', cursor: 'pointer' }}
                  title="Sound the siren here again">
            {releasing === `${camera}|${cell}` ? '…' : 'Wake it'}
          </button>
          <button type="button" onClick={onClose}
                  style={{ fontSize: 12, padding: '2px 10px', cursor: 'pointer' }}>Close</button>
        </span>
      </div>

      {e && <div style={{ fontSize: 12.5, color: '#ef4444' }}>{e}</div>}
      {!d && !e && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Loading…</div>}

      {d && (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            Every alert recorded at this spot. The solid box is what the detector
            saw; the dashed box is the spot itself.
            {d.animal_built > 0 && (
              <div style={{ marginTop: 6, color: '#92400e', fontWeight: 600 }}>
                {d.animal_built} of these had animal evidence. A spot built from
                animals silences the ground they crossed — which on a gate or a
                path is where people walk. Wake it, and it will not re-learn from
                animals again.
              </div>
            )}
          </div>
          {d.alerts.length === 0
            ? <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                No alerts left in the log for this spot — the photos are kept{' '}
                {d.image_retention_days} days and the log holds the last 500.
                The spot's own counts are in the table below.
              </div>
            : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {d.alerts.map(a => (
                  <div key={a.id || a.ts} style={{ width: 200 }}>
                    {a.snapshot
                      ? <AuthThumb file={a.snapshot} alt={`${camera} ${cell}`} width={200} height={126}
                          boxes={[{ r: a.bbox, color: a.looks_animal ? '#f59e0b' : '#2563eb' },
                                  { r: d.rect, color: '#6b7280', dashed: true }]} />
                      : <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>no photo</div>}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {a.ts ? new Date(a.ts * 1000).toLocaleString() : ''}
                    </div>
                    <div style={{ fontSize: 11, marginTop: 2 }}>
                      {a.looks_animal
                        ? <span style={pill('#fef3c7', '#92400e')}>
                            {a.animal_class || 'animal'}
                            {a.animal_conf != null ? ` ${Number(a.animal_conf).toFixed(2)}` : ''}
                          </span>
                        : <span style={{ color: 'var(--text-muted)' }}>
                            {a.kp != null ? `${a.kp} keypoints` : 'no pose'}
                          </span>}
                      {a.siren_dropped && <span style={{ ...pill('#fee2e2', '#991b1b'), marginLeft: 6 }}>
                        siren dropped</span>}
                    </div>
                  </div>
                ))}
              </div>}
        </>
      )}
    </div>
  );
}

// Editing these changes detection behaviour on a live site, so the form shows
// what is running, saves only what was actually edited, and reports what the box
// says changed — never what the form hoped would change.
function Tunables({ live, onSaved }) {
  const [draft, setDraft] = useState({});
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState(null);

  const dirty = Object.keys(draft).filter(k => String(draft[k]) !== String(live[k]));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = {};
      dirty.forEach(k => { body[k] = Number(draft[k]); });
      const r = await apiFetch('/ui/learning/tunables', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg({ ok: false, text: j.detail || `HTTP ${r.status}` }); return; }
      setMsg({ ok: true, text: 'Saved and live — no restart needed' });
      setDraft({});
      onSaved?.();
    } catch (e) {
      setMsg({ ok: false, text: String(e.message || e) });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 18, border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>Thresholds on this box</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '4px 0 12px' }}>
        Measured on two sites. Change them here for this install — applied immediately.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
        {Object.entries(live).map(([k, v]) => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
            <input
              type="number" step="any"
              value={draft[k] ?? v ?? ''}
              onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))}
              style={{ width: 76, padding: '4px 6px', border: '1px solid var(--border)',
                       borderRadius: 6, background: 'var(--surface)', color: 'inherit' }} />
            <span>
              <span style={{ fontWeight: 600 }}>{k}</span>
              <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: 11 }}>
                {TUNABLE_HELP[k] || ''}
              </span>
            </span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <button onClick={save} disabled={busy || !dirty.length} style={{ padding: '6px 14px' }}>
          {busy ? 'Saving…' : dirty.length ? `Save ${dirty.length} change${dirty.length === 1 ? '' : 's'}` : 'No changes'}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg.ok ? 'var(--ok, #16a34a)' : '#ef4444' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

// "7,2" is an engineering internal. Make it the way in to seeing the place.
function CellLink({ camera, cell, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen({ camera, cell })}
            title="Show the alerts recorded at this spot"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                     font: 'inherit', fontFamily: 'monospace', color: '#2563eb',
                     textDecoration: 'underline' }}>
      {cell}
    </button>
  );
}

export default function LearningReportPage() {
  const [data, setData]       = useState(null);
  const [err, setErr]         = useState('');
  const [loading, setLoading] = useState(false);
  const [days, setDays]       = useState(30);
  const [open, setOpen]       = useState({});
  const [releasing, setReleasing] = useState(null);
  const [focus, setFocus]     = useState(null);
  const [spot, setSpot]       = useState(null);   // {camera, cell} being looked at

  // Permanent and visible, exactly like a customer tapping Correct: the counting
  // that silenced the spot cannot silence it again.
  const release = async (camera, cell) => {
    setReleasing(`${camera}|${cell}`);
    try {
      const r = await apiFetch('/ui/learning/release', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ camera, cell }),
      });
      if (r.ok) await load();
    } catch { /* the next refresh shows the truth either way */ }
    finally { setReleasing(null); }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await apiFetch(`/ui/learning/report?days=${days}`);
      if (r.status === 403) { setErr('Admin access required.'); setData(null); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const cams = data?.cameras || [];

  // THREE states silence the siren, not one. summary() reports flagged,
  // restless and confirmed separately, and this tile used to count only
  // `flagged` — so a camera silenced by a spot that stormed all night, or by
  // one somebody confirmed was a bush, read 0 on the headline number while the
  // row underneath showed a red pill. Keep this in step with SILENCING below.
  const totWatch = cams.reduce((a, c) => a + (c.scene?.watch || 0), 0);
  const totFlag  = cams.reduce((a, c) => a + (c.scene?.flagged || 0)
                                           + (c.scene?.restless || 0)
                                           + (c.scene?.confirmed || 0), 0);
  const totNear  = cams.reduce((a, c) => a + (c.siren?.near_miss || 0), 0);
  const totSupp  = cams.reduce((a, c) => a + (c.siren?.suppressed || 0), 0);

  // Clicking a tile answers "which ones?". Without it the numbers are a dead
  // end: the detail exists, but only per-camera, behind a disclosure triangle,
  // and the suppression reasons were reachable only by hovering a table cell —
  // which is nothing at all on a tablet.
  const SILENCING = ['flagged', 'restless', 'confirmed'];
  const TILES = [
    { key: 'silencing', label: 'Spots silencing the siren', value: totFlag,
      hint: 'these drop the siren; the alert still fires', states: SILENCING,
      empty: 'No spot is silencing the siren anywhere. Every siren that fired, fired.' },
    { key: 'watch', label: 'Spots building up', value: totWatch,
      hint: 'no person resolved yet — check these first', states: ['watch'],
      empty: 'Nothing is building up.' },
    { key: 'near', label: 'Sirens kept by a real person', value: totNear,
      hint: 'flagged spot, but a skeleton resolved: the release valve working',
      metric: 'near_miss',
      empty: 'The release valve has not had to fire yet.' },
    { key: 'supp', label: 'Sirens dropped', value: totSupp,
      hint: `in the last ${data?.days} days`, metric: 'suppressed', breakdown: true,
      empty: 'No siren has been dropped in this window.' },
  ];
  const focused = TILES.find(t => t.key === focus) || null;

  // Flat across cameras deliberately: the question behind the tile is "which
  // spots", not "which spots on which camera", and per-camera is already the
  // table below.
  const focusCells = [];
  if (focused?.states) {
    cams.forEach(c => (c.scene?.cells || []).forEach(r => {
      if (focused.states.includes(r.state)) focusCells.push({ cam: c.name, ...r });
    }));
  }
  const focusCams = focused?.metric
    ? cams.filter(c => (c.siren?.[focused.metric] || 0) > 0)
    : [];

  return (
    <div style={{ padding: 20, maxWidth: 1180, margin: '0 auto' }}>
      <h2 style={{ margin: 0 }}>Learning report</h2>
      <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
        What each camera has taught itself. Admin only — cell indices and sample
        counts are engineering internals.
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '16px 0' }}>
        <label style={{ fontSize: 13 }}>Window&nbsp;
          <select value={days} onChange={e => setDays(Number(e.target.value))}
                  style={{ padding: '6px 8px' }}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </label>
        <button onClick={load} style={{ padding: '6px 14px' }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {err && <div style={{ padding: 14, border: '1px solid var(--border)', borderRadius: 8,
                            color: 'var(--text-muted)' }}>{err}</div>}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {TILES.map(t => {
              const on = focus === t.key;
              return (
                <button key={t.key} type="button" title={t.hint}
                        onClick={() => setFocus(on ? null : t.key)}
                        style={{ border: `1px solid ${on ? '#2563eb' : 'var(--border)'}`,
                                 borderRadius: 10, padding: '10px 14px', minWidth: 150,
                                 background: on ? 'var(--surface)' : 'transparent',
                                 color: 'inherit', font: 'inherit', textAlign: 'left',
                                 cursor: 'pointer' }}>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{t.value ?? '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: on ? '#2563eb' : 'var(--text-muted)',
                                marginTop: 4 }}>{on ? 'hide' : 'show these'}</div>
                </button>
              );
            })}
          </div>

          {focused && (
            <div style={{ border: '1px solid #2563eb', borderRadius: 10,
                          padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: 10 }}>
                <strong style={{ fontSize: 13 }}>{focused.label}</strong>
                <button type="button" onClick={() => setFocus(null)}
                        style={{ fontSize: 12, padding: '2px 10px', cursor: 'pointer' }}>
                  Close
                </button>
              </div>

              {/* Spots: which cell, on which camera, and the way back out. */}
              {focused.states && (focusCells.length === 0
                ? <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{focused.empty}</div>
                : <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead><tr style={{ color: 'var(--text-muted)' }}>
                        {['Camera', 'Cell', 'Alerts', 'Attempts', 'Span', 'State', ''].map(h =>
                          <th key={h} style={{ ...th, padding: '4px 14px 4px 0' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {focusCells.map(r => (
                          <tr key={`${r.cam}|${r.cell}`}>
                            <td style={{ padding: '3px 14px 3px 0', fontWeight: 600 }}>{r.cam}</td>
                            <td style={{ padding: '3px 14px 3px 0' }}>
                              <CellLink camera={r.cam} cell={r.cell} onOpen={setSpot} />
                            </td>
                            <td style={{ padding: '3px 14px 3px 0' }}>{r.n}</td>
                            <td style={{ padding: '3px 14px 3px 0' }}>{r.attempts || '—'}</td>
                            <td style={{ padding: '3px 14px 3px 0' }}>{r.span_days}d</td>
                            <td style={{ padding: '3px 14px 3px 0' }}>
                              {r.state === 'flagged'   && <span style={pill('#fee2e2', '#991b1b')}>silencing the siren</span>}
                              {r.state === 'restless'  && <span style={pill('#fee2e2', '#991b1b')}>tried all night, no person</span>}
                              {r.state === 'confirmed' && <span style={pill('#e0e7ff', '#3730a3')}>{r.label || 'confirmed not a person'}</span>}
                              {r.state === 'watch'     && <span style={pill('#fef3c7', '#92400e')}>building up</span>}
                            </td>
                            <td style={{ padding: '3px 0' }}>
                              {SILENCING.includes(r.state) && (
                                <button type="button" disabled={releasing === `${r.cam}|${r.cell}`}
                                        onClick={() => release(r.cam, r.cell)}
                                        style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}
                                        title="Sound the siren here again">
                                  {releasing === `${r.cam}|${r.cell}` ? '…' : 'Wake it'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>)}

              {/* Sirens: the actual alerts, with the photo. "Dropped" means the
                  siren stayed silent -- the alert fired and was stored like any
                  other, which is exactly what makes this checkable. */}
              {focused.metric && (() => {
                const want = focused.key === 'supp' ? 'dropped' : 'kept';
                const evs  = (data.siren_events || []).filter(e => e.kind === want);
                if (!evs.length) return (
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {focused.empty}
                    {focusCams.length > 0 && ' (older than this window — widen it above)'}
                  </div>
                );
                return (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                      {want === 'dropped'
                        ? 'The siren stayed silent for these. Each alert still fired and was saved — this is the photo it was judged on.'
                        : 'A real person was resolved at a silenced spot, so the siren sounded anyway.'}
                      {/* Records outlive photos: the log keeps the last 500 at
                          any age, images are pruned on a day count. Without
                          this the missing ones read as a broken page. */}
                      {data.image_retention_days && data.days > data.image_retention_days && (
                        <div style={{ marginTop: 4 }}>
                          Photos are kept {data.image_retention_days} days, so anything older
                          than that in this {data.days}-day window shows no photo. The alert
                          itself is still listed.
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {evs.map(e => (
                        <div key={e.id || `${e.cam}${e.ts}`} style={{ width: 148 }}>
                          {e.snapshot
                            ? <AuthThumb file={e.snapshot} alt={`${e.cam} alert`} />
                            : <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>no photo</div>}
                          <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 4 }}>{e.cam}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {e.ts ? new Date(e.ts * 1000).toLocaleString() : ''}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2,
                                        wordBreak: 'break-word' }}>{e.why}</div>
                          {e.verdict === 'false_alert' && (
                            <span style={{ ...pill('#dcfce7', '#166534'), display: 'inline-block',
                                           marginTop: 4 }}>customer said false</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {spot && (
            <SpotPanel camera={spot.camera} cell={spot.cell}
                       releasing={releasing} onRelease={release}
                       onReleased={() => setSpot(null)}
                       onClose={() => setSpot(null)} />
          )}

          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 900 }}>
              <thead><tr style={{ color: 'var(--text-muted)', background: 'var(--surface)' }}>
                {['Camera', 'At night', 'Animals', 'Scene memory', 'Heights learned',
                  'Sirens', 'Dropped', 'Kept by a person'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {cams.length === 0 && <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center',
                  color: 'var(--text-muted)' }}>No cameras.</td></tr>}
                {cams.map(c => {
                  const s = c.scene || {}, p = c.perspective, sir = c.siren || {};
                  const isOpen = !!open[c.idx];
                  return [
                    <tr key={c.idx} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ ...td, fontWeight: 600 }}>
                        {(s.cells || []).length > 0
                          ? <button type="button" onClick={() => setOpen(o => ({ ...o, [c.idx]: !o[c.idx] }))}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                       color: 'inherit', font: 'inherit', textAlign: 'left' }}>
                              {isOpen ? '▾ ' : '▸ '}{c.name}
                            </button>
                          : c.name}
                      </td>
                      <td style={td}>{c.night_posture
                        ? POSTURE_LABEL[c.night_posture] || c.night_posture
                        : <span style={pill('var(--surface)', 'var(--text-muted)')}>not asked</span>}</td>
                      <td style={td}>{c.animals_present ? 'yes' : '—'}</td>
                      <td style={td}>
                        {s.flagged ? <span style={{ ...pill('#fee2e2', '#991b1b'), marginRight: 6 }}>
                          {s.flagged} silencing</span> : null}
                        {/* Caught by the attempt count within one night, rather
                            than by alerts over two days — wind on foliage or a
                            washing line. */}
                        {s.restless ? <span style={{ ...pill('#fee2e2', '#991b1b'), marginRight: 6 }}
                          title="Kept producing blocked detections all night and never resolved a person">
                          {s.restless} restless</span> : null}
                        {s.confirmed ? <span style={{ ...pill('#e0e7ff', '#3730a3'), marginRight: 6 }}
                          title="Something looked at the picture and said what it is">
                          {s.confirmed} confirmed</span> : null}
                        {s.watch ? <span style={{ ...pill('#fef3c7', '#92400e'), marginRight: 6 }}>
                          {s.watch} building</span> : null}
                        {s.released ? <span style={pill('#dcfce7', '#166534')}>
                          {s.released} people</span> : null}
                        {!s.flagged && !s.restless && !s.confirmed && !s.watch && !s.released &&
                          <span style={{ color: 'var(--text-muted)' }}>nothing yet</span>}
                      </td>
                      <td style={td}>
                        {p
                          ? (p.model
                              ? <span title={`rows ${p.model.y_lo}–${p.model.y_hi}`}>
                                  ✅ {p.samples} samples{p.model.source === 'walk' ? ' (walk test)' : ''}
                                </span>
                              : <span style={{ color: 'var(--text-muted)' }}>
                                  {p.samples} — needs {p.needs} more
                                </span>)
                          : <span style={{ color: 'var(--text-muted)' }}>none yet</span>}
                        {/* People ARE being seen, but every box is cut by the frame
                            edge. That is a mounting angle, not a matter of waiting —
                            without this the row just reads "0 samples" forever. */}
                        {p?.clipped > 0 && (
                          <div style={{ marginTop: 3 }}>
                            <span style={pill('#fef3c7', '#92400e')}
                                  title={`${p.clipped} confirmed people were refused as samples because their box was cut off by the frame edge. This camera is angled too steeply to learn heights — more time will not fix it.`}>
                              {p.clipped} cut off by frame edge
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={td}>{sir.sirens ?? '—'}</td>
                      <td style={td} title={Object.entries(sir.suppressed_by || {})
                        .map(([k, v]) => `${k}: ${v}`).join('\n')}>{sir.suppressed || '—'}</td>
                      <td style={td}>{sir.near_miss
                        ? <span style={pill('#dcfce7', '#166534')}>{sir.near_miss}</span>
                        : '—'}</td>
                    </tr>,
                    isOpen && (
                      <tr key={`${c.idx}-cells`} style={{ background: 'var(--surface)' }}>
                        <td colSpan={8} style={{ padding: '6px 12px 12px' }}>
                          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead><tr style={{ color: 'var(--text-muted)' }}>
                              {['Cell', 'Alerts', 'Attempts', 'People resolved', 'Span', 'State', ''].map(h =>
                                <th key={h} style={{ ...th, padding: '4px 14px 4px 0' }}>{h}</th>)}
                            </tr></thead>
                            <tbody>
                              {(s.cells || []).map(r => (
                                <tr key={r.cell}>
                                  <td style={{ padding: '3px 14px 3px 0' }}>
                              <CellLink camera={r.cam} cell={r.cell} onOpen={setSpot} />
                            </td>
                                  <td style={{ padding: '3px 14px 3px 0' }}>{r.n}</td>
                                  {/* blocked detections — ~30x more numerous than alerts */}
                                  <td style={{ padding: '3px 14px 3px 0' }}>{r.attempts || '—'}</td>
                                  <td style={{ padding: '3px 14px 3px 0' }}>{r.skel}</td>
                                  <td style={{ padding: '3px 14px 3px 0' }}>{r.span_days}d</td>
                                  <td style={{ padding: '3px 14px 3px 0' }}>
                                    {r.state === 'flagged'  && <span style={pill('#fee2e2', '#991b1b')}>silencing the siren</span>}
                                    {r.state === 'restless' && <span style={pill('#fee2e2', '#991b1b')}>tried all night, no person</span>}
                                    {r.state === 'confirmed' && <span style={pill('#e0e7ff', '#3730a3')}>{r.label || 'confirmed not a person'}</span>}
                                    {r.state === 'watch'    && <span style={pill('#fef3c7', '#92400e')}>building up</span>}
                                    {r.state === 'released' && <span style={pill('#dcfce7', '#166534')}>a person was seen here</span>}
                                  </td>
                                  {/* The customer's route back is tapping Correct on
                                      an alert, which needs an alert to tap. Support
                                      looking at this page has none — without this
                                      they can see a wrongly silenced spot and need
                                      SSH to do anything about it. */}
                                  <td style={{ padding: '3px 0' }}>
                                    {['flagged', 'restless', 'confirmed'].includes(r.state) && (
                                      <button type="button" disabled={releasing === `${c.name}|${r.cell}`}
                                        onClick={() => release(c.name, r.cell)}
                                        style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}
                                        title="Sound the siren here again — same effect as a person being seen">
                                        {releasing === `${c.name}|${r.cell}` ? '…' : 'Wake it'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                            Cell = the box centroid quantised to a {data.scene_rule?.grid}×
                            {data.scene_rule?.grid} grid. A cell silences the siren at{' '}
                            {data.scene_rule?.min_occurrences} alerts spanning{' '}
                            {data.scene_rule?.min_span_days} days with no skeleton ever resolved.
                            Any resolved skeleton releases it permanently. A cell also
                            silences the siren when it and its neighbours produce many
                            blocked detections in one night between 23:00 and 06:00 —
                            measured at {data.scene_rule?.min_attempts} over{' '}
                            {data.scene_rule?.min_attempt_span_hrs}h.
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* What this box is ACTUALLY running. These were set from two sites'
              data and are overridable per install under "SCENE_MEMORY" in
              config.json — so the report must show the live values, never the
              ones the code shipped with. */}
          {data.scene_rule?.tunables && <Tunables live={data.scene_rule.tunables} onSaved={load} />}

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 14, lineHeight: 1.6 }}>
            <strong>Building up</strong> is the column to watch. A camera too far or too
            dark for pose to resolve logs real people as 0-skeleton alerts, and those
            cells eventually silence the siren there. Several on one camera means check
            the camera, not the spot.<br />
            <strong>Heights learned</strong> is instrumentation. Measured against the known
            false alerts the plausibility band rejected none of them, so it does not
            justify lowering <code>night_conf_min</code>.
          </div>
        </>
      )}
    </div>
  );
}
